import { useSyncExternalStore } from "react";

/**
 * Whether the SpaceMouse is driving the camera, and if not, that the driver is why.
 *
 * One failure state on purpose: every way the driver can be missing has the same two remedies — install
 * 3DxWare, restart the machine — because a fresh install doesn't serve until reboot, and on macOS there's
 * no "start 3DxWare" for a user to do, it's a login daemon. Splitting it finer produced advice that was
 * more precise and less true.
 *
 * "connected" means the driver answered, never that a puck is attached: the session runs between us and
 * the driver, so it survives the device being unplugged, and this API reports no device presence at all.
 */
export type SpaceMouseStatus = "idle" | "connecting" | "connected" | "unavailable";

/**
 * How a session ended: "unreachable" never worked, "lost" was working and dropped. Not user-facing —
 * both report "unavailable". It exists to route reconnects, since only a session that was working is
 * worth waiting for, and the SDK reports both through the same untyped `onDisconnect(reason)`.
 */
export type SpaceMouseFailure = "unreachable" | "lost";

let status: SpaceMouseStatus = "idle";

/**
 * Gates the Default nav icon, so only a real handshake earns it. Set by "connected", cleared by
 * "unavailable" — never by "idle" (our own teardown on Orbit/Walk) or "connecting" (reconnects),
 * which would flicker the icon through an ordinary nav-mode switch or a driver restart.
 */
let driverConfirmed = false;
const listeners = new Set<() => void>();

const emit = (): void => {
    for (const listener of listeners) {
        listener();
    }
};

export function setSpaceMouseStatus(next: SpaceMouseStatus): void {
    if (status === next) {
        return;
    }
    status = next;
    if (next === "connected") {
        driverConfirmed = true;
    } else if (next === "unavailable") {
        driverConfirmed = false;
    }
    emit();
}

/** Toggling the setting off is the manual retry: make the next session earn the icon again. */
export function clearSpaceMouseDriverConfirmed(): void {
    if (!driverConfirmed) {
        return;
    }
    driverConfirmed = false;
    emit();
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

export function useSpaceMouseStatus(): SpaceMouseStatus {
    return useSyncExternalStore(
        subscribe,
        () => status,
        () => "idle" as const,
    );
}

export function useSpaceMouseDriverConfirmed(): boolean {
    return useSyncExternalStore(
        subscribe,
        () => driverConfirmed,
        () => false,
    );
}
